import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime, take } from 'rxjs';

import { KeyValueElevatedRoleModel, KeyValueModel } from '@models/common';
import { SearchViewModel } from '@models/module/grid';
import { TenantSettingsEnum } from '@models/tenant';
import { AssignUsersDialogComponent } from '@module-instance/assignments/assign-users-dialog/assign-users-dialog.component';
import { SelectUsersParameters } from '@module-instance/assignments/assign-users-dialog/assign-users-dialog.model';
import {
  Assignments,
  AssignmentsPipe,
  AssignmentsService,
  ModuleIds,
  SyncRolePositionModel,
  SyncUsersAndRolesModel,
} from '@module-instance/assignments/common';
import { PrimaryUsersDialogComponent } from '@module-instance/assignments/primary-users-dialog/primary-users-dialog.component';
import {
  RoleOrderingDialogComponent,
  RoleOrderingDialogData,
} from '@module-instance/assignments/role-ordering-dialog/role-ordering-dialog.component';
import { CipoSelectComponent } from '@shared/components/fields/cipo-select/cipo-select.component';
import { CipoFieldTypes, CipoListControl } from '@shared/components/fields/common';
import { VirtualListModule } from '@shared/components/virtual-list';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { CipoFormModule } from '@shared/modules';
import { NotificationService, UserService, UtilsService } from '@shared/services';
import { DictionaryStore } from '@signal-store';
import { TenantStore } from '@signal-store/tenant.store';

@Component({
  selector: 'cipo-assignments',
  imports: [
    TranslateModule,
    CipoFormModule,
    MatProgressSpinnerModule,
    NgxSkeletonLoaderModule,
    CipoSelectComponent,
    VirtualListModule,
    MatMenuModule,
    AssignmentsPipe,
  ],
  templateUrl: './assignments.component.html',
  styleUrls: ['./assignments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentsComponent implements OnInit {
  translate = inject(TranslateService);
  assignmentsService = inject(AssignmentsService);
  notification = inject(NotificationService);
  utilsService = inject(UtilsService);
  userService = inject(UserService);
  dictionaryStore = inject(DictionaryStore);

  tenantStore = inject(TenantStore);
  private dialog = inject(MatDialog);

  @Output('roleschanged') rolesChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output('assignmentschanged') assignmentsChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  /* TODO: Remove this implementation. We should only have close on X */
  @Output('onclose') onClose: EventEmitter<void> = new EventEmitter<void>();
  @Input('entityinstanceid') entityInstanceId: number;
  @Input('moduleid') moduleId: number;
  @Input('hasupdate') hasUpdate: boolean;

  MODULE_IDS = ModuleIds;
  assignments = signal<Assignments[]>([]);
  initialAssignments: Assignments[] = [];
  initialSearchAssignments = computed(() => {
    const searchValue = this.searchValue();
    return this.assignments()?.filter(
      row =>
        row?.userName?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
        row?.userOrgName?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
        row?.userEmail?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
        row?.roles?.some(r => r.value?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase())),
    );
  });
  anyAssignmentsChanged = computed(() => {
    return this.assignments()?.some(a => a?.deleting || a?.edited || a?.isNew);
  });
  totalAllocationHours = computed(() => {
    return this.assignments()
      ?.map(d => d?.allocationHoursPerMonth ?? 0)
      ?.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
  });
  searchModel: SearchViewModel = {};
  searchValue = signal('');
  searchControl = new FormControl('');
  propagateHierarchy = signal(false);
  loading = signal(true);
  saving = signal(false);
  ordering = signal(false);
  userRateEnabled = signal(false);
  userAllocationEnabled = signal(false);
  roles: KeyValueElevatedRoleModel<number, string>[] = [];
  arrayControls: Record<number, CipoListControl> = {};
  disabledForm = computed(() => this.ordering() || this.saving() || this.entityInstanceLocked());

  entityInstanceLocked = signal(false);
  @Input('entityinstancelocked')
  set _entityInstanceLocked(value: boolean) {
    this.entityInstanceLocked.set(value);

    if (Object.keys(this.arrayControls).length) {
      this.disableEnableControls(value);
    }
  }

  constructor() {
    effect(() => {
      const disabled = this.ordering() || this.saving();

      // Check to avoid multiplication
      if (Object.values(this.arrayControls)[0]?.disabled === disabled) {
        return;
      }

      this.disableEnableControls(disabled);
    });
  }

  ngOnInit(): void {
    const tenantSettings = this.tenantStore.getSettings([
      TenantSettingsEnum.user_allocation,
      TenantSettingsEnum.user_rate,
    ]);
    this.userRateEnabled.set(
      this.utilsService.convertValueToBoolean(
        tenantSettings.find(tenantSetting => tenantSetting.id == TenantSettingsEnum.user_rate).value,
      ),
    );
    this.userAllocationEnabled.set(
      this.utilsService.convertValueToBoolean(
        tenantSettings.find(tenantSetting => tenantSetting.id == TenantSettingsEnum.user_allocation).value,
      ),
    );

    this.searchControl.valueChanges
      .pipe(debounceTime(DEBOUNCE_TIMES.default))
      .subscribe(value => this.searchValue.set(value));
    this.dictionaryStore.getRoles().subscribe(roles => {
      this.roles = roles;
      this.loadData();
    });
  }

  disableEnableControls(disabled: boolean) {
    Object.values(this.arrayControls).forEach(control => {
      if (disabled) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  roleNames(userId: number): string[] {
    const roleIds = this.arrayControls[userId]?.value ?? [];
    return this.roles.filter(role => roleIds.includes(role.key)).map(role => role.value);
  }

  loadData() {
    // clear criteria and assignments
    this.searchModel.criteria = '';
    this.assignments.set([]);
    this.loading.set(true);

    this.assignmentsService.getAssignments(this.searchModel, this.entityInstanceId).subscribe(a => {
      this.assignments.set(a.data);
      this.initialAssignments = structuredClone(this.assignments());
      this.generateControls();
      this.loading.set(false);
    });
  }

  generateControls() {
    this.arrayControls = {};

    for (let index = 0; index < this.assignments().length; index++) {
      const assignment = this.assignments()[index];
      assignment.roles = assignment.roles || [];
      assignment.roleIds = assignment.roleIds || [];

      const assignmentRoles = structuredClone(this.roles).map(({ key, value, invalid }) => ({
        key,
        value,
        disabled: invalid,
      }));

      assignmentRoles.forEach(role => {
        const isElevated = this.roles.some(r => r.key === role.key && r.isElevated);
        const isGlobal = assignment.globalRoles.some(r => r.key === role.key);
        const isAssigned = assignment.roles.some(r => r.key === role.key);

        // only global elevated roles can be assigned to the user
        if (isElevated && !isGlobal && !role.disabled) {
          role.disabled = true;
        }

        // if the role is already assigned, it should not be disabled
        if (isAssigned) {
          role.disabled = false;
        }
      });

      const control = new CipoListControl(assignment.roleIds, {
        label: 'assignments.roles',
        multiple: true,
        type: CipoFieldTypes.Select,
        options: assignmentRoles,
      });

      control.valueChanges.pipe(take(1)).subscribe(() => {
        this.assignments.update(assignments => {
          assignments[index].edited = true;
          return [...assignments];
        });
        this.rolesChanged.emit(true);
      });

      this.arrayControls[assignment.userId] = control;
    }

    if (this.entityInstanceLocked()) {
      this.disableEnableControls(true);
    }
  }

  deleteAssignment(userId: number) {
    this.rolesChanged.emit(true);

    this.assignments.update(assignments => {
      assignments.find(a => a.userId === userId)!.deleting = true;
      return [...assignments];
    });
    this.arrayControls[userId].setValue([]);
    this.arrayControls[userId].disable();
  }

  restoreDeleteAssignment(userId: number) {
    const assignment = this.assignments().find(item => item.userId === userId);
    if (!assignment) return;

    this.assignments.update(assignments => {
      assignments.find(a => a.userId === assignment.userId)!.deleting = false;
      return [...assignments];
    });
    this.arrayControls[assignment.userId].setValue(assignment.roleIds);
    this.arrayControls[assignment.userId].enable();
  }

  somethingChange(userId: number) {
    this.assignments.update(assignments => {
      assignments.find(a => a.userId === userId)!.edited = true;
      return [...assignments];
    });
    this.rolesChanged.emit(true);
  }

  saveAssignments() {
    // clear search on save
    this.clearInput();

    if (this.assignments()?.length) {
      this.saving.set(true);

      for (let index = 0; index < this.assignments().length; index++) {
        const roleIds = this.arrayControls[this.assignments()[index].userId]?.value ?? [];

        this.assignments()[index].roleIds = roleIds;
        this.assignments()[index].roles = this.roles.filter(r => roleIds.includes(r.key));
      }

      const syncModel: SyncUsersAndRolesModel = {
        id: this.entityInstanceId,
        propagateOnHierarchy: this.propagateHierarchy(),
        users: this.assignments().filter(a => !a.deleting),
      };

      this.assignmentsService.syncAssignments(syncModel).subscribe({
        complete: () => {
          this.notification.success('assignments.successfully');
          this.loadData();
          this.rolesChanged.emit(false);
          this.assignmentsChanged.emit(true);
          this.saving.set(false);
          this.propagateHierarchy.set(false);
        },
        error: () => {
          this.saving.set(false);
        },
      });
    }
  }

  clearInput() {
    this.searchControl.setValue('');
  }

  openSelectUsersModel() {
    const params: SelectUsersParameters = {
      entityInstanceId: this.entityInstanceId,
      existingUsers: this.assignments()
        .filter(a => !a.deleting)
        .map(a => a.userId),
    };

    this.dialog
      .open<AssignUsersDialogComponent, SelectUsersParameters, Assignments[]>(AssignUsersDialogComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        ...this.userService.getResponsiveDialogSize().md,
        data: params,
      })
      .afterClosed()
      .subscribe(assignments => {
        if (assignments?.length) {
          // clear search on save
          this.clearInput();

          this.assignments.set([...assignments, ...this.assignments()]);
          this.generateControls();

          this.assignmentsChanged.emit(true);
        }
      });
  }

  openPrimaryUsersModel() {
    this.dialog
      .open(PrimaryUsersDialogComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        data: this.entityInstanceId,
        ...this.userService.getResponsiveDialogSize().sm,
      })
      .afterClosed()
      .subscribe((status: boolean) => {
        if (status) {
          this.loadData();
        }
      });
  }

  virtualTrackBy(_index: number, assignment: Assignments): number {
    return assignment.userId;
  }

  openRoleOrderingDialog(assignment: Assignments) {
    this.ordering.set(true);

    this.dialog
      .open<RoleOrderingDialogComponent, RoleOrderingDialogData, KeyValueModel<number, string>[]>(
        RoleOrderingDialogComponent,
        {
          panelClass: ['cipo-dialog', 'classic'],
          ...this.userService.getResponsiveDialogSize().sm,
          data: {
            userName: assignment.userName,
            roles: structuredClone(assignment.roles),
          },
        },
      )
      .afterClosed()
      .subscribe(roles => {
        this.ordering.set(false);
        if (roles) this.saveOrdering(assignment.userId, roles);
      });
  }

  private saveOrdering(userId: number, roles: KeyValueModel<number, string>[]) {
    const assignment = this.assignments().find(item => item.userId === userId);
    if (!assignment) return;

    const orderedRoles = roles.map((role, index) => ({ ...role, order: index + 1 }));

    this.assignments.update(assignments => {
      const index = assignments.findIndex(a => a.userId === userId);
      if (index !== -1) {
        assignments[index] = { ...assignments[index], roles: orderedRoles };
      }
      return [...assignments];
    });

    const model: SyncRolePositionModel = {
      entityInstanceId: this.entityInstanceId,
      userId,
      roles: orderedRoles,
    };

    this.assignmentsService.syncRolePosition(model).subscribe(() => {
      this.notification.success('assignments.rolesSuccessfully');
      this.ordering.set(false);
      this.loadData();
    });
  }

  discardChanges() {
    // clear search on save
    this.clearInput();
    this.assignments.set(structuredClone(this.initialAssignments));
    this.propagateHierarchy.set(false);
    this.generateControls();
    this.rolesChanged.emit(false);
  }

  onCloseEmit() {
    this.onClose.emit();
  }
}
